import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Input,
  Button,
  Spin,
  Empty,
  message,
  Tag,
  Space,
  Tooltip,
  Badge,
  Typography,
  Tree,
  InputNumber,
  Radio,
  Drawer,
  Divider,
  Select,
} from 'antd';
import {
  SearchOutlined,
  ReloadOutlined,
  ApartmentOutlined,
  FilterOutlined,
  TableOutlined,
  UpOutlined,
  DownOutlined,
  CloseOutlined,
  BranchesOutlined,
  ClearOutlined,
} from '@ant-design/icons';
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  useReactFlow,
  ReactFlowProvider,
  MarkerType,
  Panel,
  applyNodeChanges,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import ELK from 'elkjs/lib/elk.bundled.js';
import ERDTableNode from '../components/ERDTableNode';
import { getDataModel } from '../services/dataService';

const { Text } = Typography;
const elk = new ELK();

// ─── Schema group node ───
function SchemaGroupNode({ data }) {
  return (
    <div style={{ width: '100%', height: '100%', position: 'relative' }}>
      <div
        style={{
          position: 'absolute',
          top: -44,
          left: 12,
          fontSize: 32,
          fontWeight: 800,
          color: 'var(--theme-textPrimary, #333)',
          opacity: 0.85,
          letterSpacing: 0.5,
          whiteSpace: 'nowrap',
          zIndex: 10,
          pointerEvents: 'none',
          userSelect: 'none',
        }}
      >
        {data.label}
      </div>
    </div>
  );
}

const nodeTypes = { erdTable: ERDTableNode, schemaGroup: SchemaGroupNode };

// ─── Color palettes (for schema groups) ───
const SCHEMA_BG_COLORS = [
  'rgba(22,119,255,0.06)', 'rgba(82,196,26,0.06)', 'rgba(250,173,20,0.06)',
  'rgba(245,34,45,0.06)', 'rgba(114,46,209,0.06)', 'rgba(19,194,194,0.06)',
  'rgba(250,84,28,0.06)', 'rgba(47,84,235,0.06)', 'rgba(235,47,150,0.06)',
  'rgba(160,217,17,0.06)',
];
const SCHEMA_BORDER_COLORS = [
  'rgba(22,119,255,0.25)', 'rgba(82,196,26,0.25)', 'rgba(250,173,20,0.25)',
  'rgba(245,34,45,0.25)', 'rgba(114,46,209,0.25)', 'rgba(19,194,194,0.25)',
  'rgba(250,84,28,0.25)', 'rgba(47,84,235,0.25)', 'rgba(235,47,150,0.25)',
  'rgba(160,217,17,0.25)',
];

// ─── Table type classification: Hub / Satellite / Link ───
const TABLE_TYPE_COLORS = {
  hub: '#722ed1',   // violet
  sat: '#1677ff',   // blue
  link: '#52c41a',  // green
};

function classifyTableType(tableName) {
  const upper = tableName.toUpperCase();
  if (/_LINK_TO_/.test(upper)) return 'link';
  if (/__/.test(upper) || /_FROM_/.test(upper) || /_BY_/.test(upper)) return 'sat';
  return 'hub';
}

// ─── Graph algorithms (from Ontology page) ───
function getConnectedViews(startIds, relationships, levels) {
  const connected = new Set(startIds);
  let currentLevel = new Set(startIds);
  for (let i = 0; i < levels; i++) {
    const nextLevel = new Set();
    for (const nodeId of currentLevel) {
      for (const rel of relationships) {
        if (rel.source === nodeId) nextLevel.add(rel.target);
        if (rel.target === nodeId) nextLevel.add(rel.source);
      }
    }
    nextLevel.forEach(n => connected.add(n));
    currentLevel = nextLevel;
    if (currentLevel.size === 0) break;
  }
  return connected;
}

function getConnectedPathViews(startIds, relationships) {
  // Build undirected adjacency list
  const adj = {};
  for (const rel of relationships) {
    if (!adj[rel.source]) adj[rel.source] = new Set();
    if (!adj[rel.target]) adj[rel.target] = new Set();
    adj[rel.source].add(rel.target);
    adj[rel.target].add(rel.source);
  }
  const result = new Set(startIds);
  const ids = Array.from(startIds);

  // For each pair, find THE single shortest path (not all shortest paths)
  // to avoid pulling in irrelevant branches
  for (let i = 0; i < ids.length; i++) {
    for (let j = i + 1; j < ids.length; j++) {
      const start = ids[i], end = ids[j];
      if (!adj[start] || !adj[end]) continue;

      // BFS tracking single parent per node for one shortest path
      const parent = { [start]: null };
      const queue = [start];
      let found = false;
      while (queue.length > 0 && !found) {
        const node = queue.shift();
        for (const neighbor of (adj[node] || [])) {
          if (parent[neighbor] === undefined) {
            parent[neighbor] = node;
            if (neighbor === end) { found = true; break; }
            queue.push(neighbor);
          }
        }
      }
      if (!found) continue;

      // Walk back from end to start along single shortest path
      let cur = end;
      while (cur !== null) {
        result.add(cur);
        cur = parent[cur];
      }
    }
  }
  return result;
}

// ─── Layout engine (ELK-based, flat layout with schema overlays) ───
const NODE_WIDTH = 260;
const COL_HEIGHT = 26;
const NODE_HEIGHT_BASE = 60;
const HEADER_HEIGHT = 38;
const SCHEMA_PADDING = 28;
const SCHEMA_HEADER = 52;

function getNodeHeight(table, expandedSet) {
  const keyCount = table.columns.filter(c => c.isFK || c.isPK).length;
  const totalCount = table.columns.length;
  const isExpanded = expandedSet.has(table.id);
  const visibleCols = isExpanded ? Math.min(totalCount, 12) : keyCount;
  const hasToggle = totalCount > keyCount;
  return NODE_HEIGHT_BASE + visibleCols * COL_HEIGHT + (hasToggle ? 28 : 0);
}

async function elkLayout(tables, relationships, expandedSet) {
  // Group tables by schema for hierarchical compound layout
  const schemaGroups = {};
  for (const table of tables) {
    const schema = table.schema || 'DEFAULT';
    if (!schemaGroups[schema]) schemaGroups[schema] = [];
    schemaGroups[schema].push(table);
  }
  const schemaNames = Object.keys(schemaGroups).sort();

  // Build hierarchical ELK graph — schemas as compound nodes containing tables
  const elkGraph = {
    id: 'root',
    layoutOptions: {
      'elk.algorithm': 'layered',
      'elk.direction': 'RIGHT',
      'elk.hierarchyHandling': 'INCLUDE_CHILDREN',
      'elk.layered.spacing.nodeNodeBetweenLayers': '120',
      'elk.spacing.nodeNode': '50',
      'elk.layered.nodePlacement.strategy': 'NETWORK_SIMPLEX',
      'elk.layered.crossingMinimization.strategy': 'LAYER_SWEEP',
      'elk.layered.crossingMinimization.greedySwitch.type': 'TWO_SIDED',
      'elk.separateConnectedComponents': 'true',
      'elk.spacing.componentComponent': '80',
      'elk.layered.edgeRouting': 'ORTHOGONAL',
      'elk.layered.unnecessaryBendpoints': 'false',
    },
    children: schemaNames.map(schema => ({
      id: `schema-${schema}`,
      layoutOptions: {
        'elk.padding': '[top=24,left=30,bottom=30,right=30]',
      },
      children: schemaGroups[schema].map(table => ({
        id: table.id,
        width: NODE_WIDTH,
        height: getNodeHeight(table, expandedSet),
      })),
    })),
    edges: relationships.map((r, i) => ({
      id: `e-${r.source}-${r.target}-${i}`,
      sources: [r.source],
      targets: [r.target],
    })),
  };

  const layoutResult = await elk.layout(elkGraph);

  const nodes = [];

  // Process schema compound nodes and their child tables
  for (let si = 0; si < layoutResult.children.length; si++) {
    const schemaLayout = layoutResult.children[si];
    const schemaName = schemaLayout.id.replace('schema-', '');
    const colorIdx = schemaNames.indexOf(schemaName) % SCHEMA_BG_COLORS.length;

    // Schema group node (parent — draggable, children move with it)
    nodes.push({
      id: schemaLayout.id,
      type: 'schemaGroup',
      position: { x: schemaLayout.x, y: schemaLayout.y },
      style: {
        width: schemaLayout.width,
        height: schemaLayout.height,
        background: SCHEMA_BG_COLORS[colorIdx],
        border: `1.5px solid ${SCHEMA_BORDER_COLORS[colorIdx]}`,
        borderRadius: 12, padding: 0,
      },
      data: { label: schemaName },
      selectable: false,
      draggable: true,
    });

    // Table nodes — positioned relative to their schema parent
    for (const tableLayout of (schemaLayout.children || [])) {
      const table = tables.find(t => t.id === tableLayout.id);
      if (!table) continue;
      const tableType = classifyTableType(table.name);
      nodes.push({
        id: table.id,
        type: 'erdTable',
        position: { x: tableLayout.x, y: tableLayout.y },
        parentId: schemaLayout.id,
        data: {
          label: table.name,
          tableId: table.id,
          schema: table.schema,
          columns: table.columns,
          highlighted: false,
          expanded: expandedSet.has(table.id),
          tableType,
          accessDenied: !!table.access_denied,
        },
      });
    }
  }
  return nodes;
}

// ─── Auto-resize schema containers to wrap their tables after drag ───
const GROUP_PADDING = 30;
function resizeSchemaGroups(nodes) {
  const childrenByParent = {};
  for (const node of nodes) {
    if (node.parentId) {
      if (!childrenByParent[node.parentId]) childrenByParent[node.parentId] = [];
      childrenByParent[node.parentId].push(node);
    }
  }
  const adjustments = {};
  for (const [parentId, children] of Object.entries(childrenByParent)) {
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const child of children) {
      const cw = child.measured?.width || NODE_WIDTH;
      const ch = child.measured?.height || 100;
      minX = Math.min(minX, child.position.x);
      minY = Math.min(minY, child.position.y);
      maxX = Math.max(maxX, child.position.x + cw);
      maxY = Math.max(maxY, child.position.y + ch);
    }
    const dx = minX < GROUP_PADDING ? GROUP_PADDING - minX : 0;
    const dy = minY < GROUP_PADDING ? GROUP_PADDING - minY : 0;
    adjustments[parentId] = {
      dx, dy,
      newWidth: maxX + dx + GROUP_PADDING,
      newHeight: maxY + dy + GROUP_PADDING,
    };
  }
  return nodes.map(node => {
    if (node.type === 'schemaGroup') {
      const adj = adjustments[node.id];
      if (!adj) return node;
      return {
        ...node,
        position: (adj.dx > 0 || adj.dy > 0)
          ? { x: node.position.x - adj.dx, y: node.position.y - adj.dy }
          : node.position,
        style: { ...node.style, width: adj.newWidth, height: adj.newHeight },
      };
    }
    if (node.parentId) {
      const adj = adjustments[node.parentId];
      if (!adj || (adj.dx === 0 && adj.dy === 0)) return node;
      return { ...node, position: { x: node.position.x + adj.dx, y: node.position.y + adj.dy } };
    }
    return node;
  });
}

function buildEdges(relationships, showLabels, tables, expandedSet, edgeType = 'bezier') {
  // Build map of displayed column names per table for handle targeting
  const displayedColumnsMap = {};
  for (const table of tables) {
    const cols = table.columns || [];
    const isExpanded = expandedSet.has(table.id);
    const displayCols = isExpanded ? cols : cols.filter(c => c.isFK || c.isPK);
    displayedColumnsMap[table.id] = new Set(displayCols.map(c => c.name));
  }

  return relationships.map((rel, idx) => {
    const srcDisplayed = displayedColumnsMap[rel.source];
    const tgtDisplayed = displayedColumnsMap[rel.target];
    const sourceHandle = (rel.joinColumn && srcDisplayed && srcDisplayed.has(rel.joinColumn))
      ? `col-${rel.joinColumn}` : 'default';
    const targetHandle = (rel.foreignKey && tgtDisplayed && tgtDisplayed.has(rel.foreignKey))
      ? `col-${rel.foreignKey}` : 'default';

    return {
      id: `e-${rel.source}-${rel.target}-${idx}`,
      source: rel.source,
      target: rel.target,
      sourceHandle,
      targetHandle,
      type: edgeType,
      animated: false,
      ...(edgeType === 'smoothstep' ? { pathOptions: { borderRadius: 12, offset: 20 } } : {}),
      label: showLabels ? (rel.joinColumn || '') : '',
      labelStyle: { fontSize: 10, fill: '#999' },
      labelBgStyle: { fill: 'var(--theme-cardBg, #fff)', fillOpacity: 0.8 },
      labelBgPadding: [4, 2],
      style: { stroke: '#faad14', strokeWidth: 1.5 },
      markerEnd: { type: MarkerType.ArrowClosed, color: '#faad14', width: 14, height: 14 },
    };
  });
}

// ─── Inner component (needs ReactFlowProvider for useReactFlow) ───
function DataModelInner() {
  const reactFlowInstance = useReactFlow();

  const [allTables, setAllTables] = useState([]);
  const [allRelationships, setAllRelationships] = useState([]);
  const [loading, setLoading] = useState(false);

  // Search state (Chrome-like)
  const [searchText, setSearchText] = useState('');
  const [searchMatches, setSearchMatches] = useState([]);
  const [searchIdx, setSearchIdx] = useState(0);

  // Filter state
  const [filterOpen, setFilterOpen] = useState(false);
  const [checkedKeys, setCheckedKeys] = useState([]);
  const [filterTreeSearch, setFilterTreeSearch] = useState('');
  const [filterMode, setFilterMode] = useState('connected_paths');
  const [relationshipLevels, setRelationshipLevels] = useState(1);
  const [activeFilter, setActiveFilter] = useState(null);

  // Display state
  const [showEdgeLabels] = useState(false);
  const [edgeType, setEdgeType] = useState('bezier');
  const [expandedTables, setExpandedTables] = useState(new Set());
  const [fitViewTrigger, setFitViewTrigger] = useState(0);

  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  // ── Load data ──
  const loadDataModel = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getDataModel();
      if (data.empty) {
        message.info(data.message || 'No data model available. Run a catalog scan first.');
        setAllTables([]); setAllRelationships([]); setNodes([]); setEdges([]);
        return;
      }
      // Mark columns referenced as FK targets (typically PKs)
      const rels = data.relationships || [];
      const tables = data.tables || [];
      const referencedCols = {};
      for (const rel of rels) {
        if (rel.foreignKey && rel.target) {
          if (!referencedCols[rel.target]) referencedCols[rel.target] = new Set();
          referencedCols[rel.target].add(rel.foreignKey);
        }
      }
      for (const table of tables) {
        const refCols = referencedCols[table.id];
        if (refCols) {
          for (const col of table.columns) {
            if (refCols.has(col.name)) col.isPK = true;
          }
        }
      }
      setAllTables(tables);
      setAllRelationships(rels);
      setActiveFilter(null);
      setCheckedKeys([]);
      setExpandedTables(new Set());
    } catch (error) {
      message.error('Failed to load data model');
    } finally {
      setLoading(false);
    }
  }, [setNodes, setEdges]);

  useEffect(() => { loadDataModel(); }, [loadDataModel]);

  // ── Tree data for filter drawer ──
  const treeData = useMemo(() => {
    const schemaMap = {};
    for (const t of allTables) {
      const s = t.schema || 'DEFAULT';
      if (!schemaMap[s]) schemaMap[s] = [];
      schemaMap[s].push(t);
    }
    const lower = filterTreeSearch.toLowerCase();
    return Object.keys(schemaMap).sort().map(schema => {
      let children = schemaMap[schema].sort((a, b) => a.name.localeCompare(b.name));
      if (lower) {
        children = children.filter(t =>
          t.name.toLowerCase().includes(lower) ||
          t.schema.toLowerCase().includes(lower) ||
          t.qualified.toLowerCase().includes(lower)
        );
      }
      if (lower && children.length === 0) return null;
      return {
        title: `${schema} (${children.length})`,
        key: `schema:${schema}`,
        children: children.map(t => ({ title: t.name, key: t.id })),
      };
    }).filter(Boolean);
  }, [allTables, filterTreeSearch]);

  // ── Filtered tables ──
  const filteredTables = useMemo(() => {
    if (!activeFilter) return allTables;
    let selectedIds = new Set(activeFilter.tableIds);
    if (selectedIds.size === 0) return allTables;
    if (activeFilter.mode === 'relationship_levels') {
      selectedIds = getConnectedViews(selectedIds, allRelationships, activeFilter.levels);
    } else {
      selectedIds = getConnectedPathViews(selectedIds, allRelationships);
    }
    return allTables.filter(t => selectedIds.has(t.id));
  }, [allTables, allRelationships, activeFilter]);

  // ── Filtered relationships ──
  const filteredRelationships = useMemo(() => {
    const tableIds = new Set(filteredTables.map(t => t.id));
    return allRelationships.filter(r => tableIds.has(r.source) && tableIds.has(r.target));
  }, [filteredTables, allRelationships]);

  // ── Toggle expand callback ──
  const onToggleExpand = useCallback((tableId) => {
    setExpandedTables(prev => {
      const next = new Set(prev);
      if (next.has(tableId)) next.delete(tableId);
      else next.add(tableId);
      return next;
    });
  }, []);

  // ── Search: compute matches ──
  useEffect(() => {
    if (!searchText.trim()) {
      setSearchMatches([]);
      setSearchIdx(0);
      return;
    }
    const lower = searchText.toLowerCase();
    const matches = filteredTables
      .filter(t => t.name.toLowerCase().includes(lower) || t.qualified.toLowerCase().includes(lower))
      .map(t => t.id);
    setSearchMatches(matches);
    setSearchIdx(matches.length > 0 ? 0 : -1);
  }, [searchText, filteredTables]);

  // ── Focus on current search match ──
  useEffect(() => {
    if (searchMatches.length === 0 || searchIdx < 0) return;
    const targetId = searchMatches[searchIdx];
    const node = nodes.find(n => n.id === targetId);
    if (!node || !reactFlowInstance) return;
    const absX = node.position.x;
    const absY = node.position.y;
    reactFlowInstance.setCenter(absX + 130, absY + 100, { zoom: 1.2, duration: 500 });
  }, [searchIdx, searchMatches, nodes, reactFlowInstance]);

  // ── Navigate search ──
  const goNextMatch = useCallback(() => {
    if (searchMatches.length === 0) return;
    setSearchIdx(prev => (prev + 1) % searchMatches.length);
  }, [searchMatches]);
  const goPrevMatch = useCallback(() => {
    if (searchMatches.length === 0) return;
    setSearchIdx(prev => (prev - 1 + searchMatches.length) % searchMatches.length);
  }, [searchMatches]);

  const onSearchKeyDown = useCallback((e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (e.shiftKey) goPrevMatch(); else goNextMatch();
    }
    if (e.key === 'Escape') setSearchText('');
  }, [goNextMatch, goPrevMatch]);

  // ── Node change handler with schema-group auto-resize ──
  const handleNodesChange = useCallback((changes) => {
    const hasDragEnd = changes.some(c => c.type === 'position' && c.dragging === false && c.position);
    if (hasDragEnd) {
      setNodes(nds => resizeSchemaGroups(applyNodeChanges(changes, nds)));
    } else {
      onNodesChange(changes);
    }
  }, [onNodesChange, setNodes]);

  // ── Update nodes and edges ──
  // Track whether this is the initial layout (need fitView) vs expand/collapse (preserve viewport)
  const isInitialLayoutRef = React.useRef(true);
  const prevFilteredTablesRef = React.useRef(filteredTables);
  useEffect(() => {
    if (prevFilteredTablesRef.current !== filteredTables) {
      isInitialLayoutRef.current = true;
      prevFilteredTablesRef.current = filteredTables;
    }
  }, [filteredTables]);

  useEffect(() => {
    if (filteredTables.length === 0) {
      setNodes([]); setEdges([]);
      return;
    }
    const shouldFitView = isInitialLayoutRef.current;
    isInitialLayoutRef.current = false;
    let cancelled = false;
    elkLayout(filteredTables, filteredRelationships, expandedTables).then(layoutedNodes => {
      if (cancelled) return;
      const currentMatchId = searchMatches.length > 0 && searchIdx >= 0 ? searchMatches[searchIdx] : null;
      const displayNodes = layoutedNodes.map(node => {
        if (node.type === 'schemaGroup') return node;
        return {
          ...node,
          data: {
            ...node.data,
            highlighted: node.id === currentMatchId,
            expanded: expandedTables.has(node.id),
            onToggleExpand,
          },
        };
      });
      setNodes(displayNodes);
      setEdges(buildEdges(filteredRelationships, showEdgeLabels, filteredTables, expandedTables, edgeType));
      // Only fitView on initial layout or filter change, not on expand/collapse
      if (shouldFitView) {
        setFitViewTrigger(c => c + 1);
      }
    }).catch(err => {
      console.error('ELK layout failed:', String(err?.message || err).replace(/[\r\n]/g, ' '));
    });
    return () => { cancelled = true; };
  }, [filteredTables, filteredRelationships, showEdgeLabels, expandedTables, edgeType, searchMatches, searchIdx, onToggleExpand, setNodes, setEdges, setFitViewTrigger]);

  // ── Fit view after filter/layout changes ──
  useEffect(() => {
    if (fitViewTrigger === 0 || !reactFlowInstance) return;
    const timer = setTimeout(() => {
      reactFlowInstance.fitView({ padding: 0.3, duration: 500 });
    }, 300);
    return () => clearTimeout(timer);
  }, [fitViewTrigger, reactFlowInstance]);

  // ── Filter actions ──
  const applyFilter = useCallback(() => {
    const tableIds = checkedKeys.filter(k => !k.startsWith('schema:'));
    if (tableIds.length === 0) {
      message.warning('Select at least one view');
      return;
    }
    setActiveFilter({ tableIds, mode: filterMode, levels: relationshipLevels });
    setFilterOpen(false);
    message.success(`Filter applied: ${tableIds.length} view(s)`);
  }, [checkedKeys, filterMode, relationshipLevels]);

  const clearFilter = useCallback(() => {
    setActiveFilter(null);
    setCheckedKeys([]);
    isInitialLayoutRef.current = true;
    message.success('Filter cleared');
  }, []);

  const visibleTables = filteredTables.length;
  const visibleRelationships = filteredRelationships.length;

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: 'var(--theme-contentBg)' }}>
      {/* ── Toolbar ── */}
      <div style={{
        padding: '10px 20px',
        borderBottom: '1px solid var(--theme-border, #f0f0f0)',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        flexWrap: 'wrap',
        background: 'var(--theme-cardBg, #fff)',
      }}>
        <ApartmentOutlined style={{ fontSize: 20, color: 'var(--theme-menuSelected, #1677ff)' }} />
        <Text strong style={{ fontSize: 16, marginRight: 4 }}>Data Model</Text>

        {/* Filter button */}
        <Tooltip title="Open filter panel">
          <Button icon={<FilterOutlined />} onClick={() => setFilterOpen(true)} type={activeFilter ? 'primary' : 'default'} size="small">
            Filter
          </Button>
        </Tooltip>

        {activeFilter && (
          <Tag color="blue" closable onClose={clearFilter} style={{ margin: 0 }}>
            {activeFilter.tableIds.length} views · {activeFilter.mode === 'connected_paths' ? 'Connected Paths' : `${activeFilter.levels} levels`}
          </Tag>
        )}

        <Button icon={<ReloadOutlined />} onClick={loadDataModel} size="small">Refresh</Button>

        <Select
          size="small"
          value={edgeType}
          onChange={setEdgeType}
          style={{ width: 110 }}
          options={[
            { value: 'bezier', label: 'Curved' },
            { value: 'straight', label: 'Straight' },
            { value: 'smoothstep', label: 'Step' },
          ]}
        />

        <div style={{ marginLeft: 'auto', display: 'flex', gap: 12 }}>
          <Badge count={visibleTables} overflowCount={999} style={{ backgroundColor: '#1677ff' }}>
            <Tag icon={<TableOutlined />}>Tables</Tag>
          </Badge>
          <Badge count={visibleRelationships} overflowCount={999} style={{ backgroundColor: '#faad14' }}>
            <Tag icon={<ApartmentOutlined />}>Relations</Tag>
          </Badge>
        </div>
      </div>

      {/* ── Filter Drawer ── */}
      <Drawer
        title={<Space><FilterOutlined /> Filter Views</Space>}
        placement="left"
        width={480}
        open={filterOpen}
        onClose={() => setFilterOpen(false)}
        extra={
          <Space>
            <Button size="small" onClick={() => setCheckedKeys([])}>Clear Selection</Button>
            <Button size="small" type="primary" onClick={applyFilter}>Apply</Button>
          </Space>
        }
      >
        <div style={{ marginBottom: 16 }}>
          <Text strong style={{ display: 'block', marginBottom: 8 }}>Select Views:</Text>
          <Input
            prefix={<SearchOutlined />}
            placeholder="Search schemas or tables..."
            value={filterTreeSearch}
            onChange={e => setFilterTreeSearch(e.target.value)}
            allowClear
            style={{ marginBottom: 8 }}
          />
          <Tree
            checkable
            checkedKeys={checkedKeys}
            onCheck={(checked) => {
              // Merge: keep checked keys for items hidden by search filter
              const visibleKeys = new Set();
              for (const node of treeData) {
                visibleKeys.add(node.key);
                for (const child of (node.children || [])) visibleKeys.add(child.key);
              }
              const hiddenChecked = checkedKeys.filter(k => !visibleKeys.has(k));
              setCheckedKeys([...hiddenChecked, ...checked]);
            }}
            treeData={treeData}
            height={320}
            style={{ border: '1px solid var(--theme-border, #f0f0f0)', borderRadius: 6, padding: 4 }}
            showLine
          />
        </div>

        <Divider style={{ margin: '12px 0' }} />

        <div style={{ marginBottom: 16 }}>
          <Text strong style={{ display: 'block', marginBottom: 8 }}>Display Mode:</Text>
          <Radio.Group value={filterMode} onChange={e => setFilterMode(e.target.value)} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <Radio value="connected_paths">
              <Space><BranchesOutlined /> Connected Paths</Space>
              <div style={{ fontSize: 11, color: '#999', marginLeft: 24 }}>
                Selected views + intermediate views on shortest connecting paths
              </div>
            </Radio>
            <Radio value="relationship_levels">
              <Space><ApartmentOutlined /> Relationship Levels</Space>
              <div style={{ fontSize: 11, color: '#999', marginLeft: 24 }}>
                All views within N hops from selected views
              </div>
            </Radio>
          </Radio.Group>
        </div>

        {filterMode === 'relationship_levels' && (
          <div style={{ marginBottom: 16 }}>
            <Text strong>Levels: </Text>
            <InputNumber min={1} max={10} value={relationshipLevels} onChange={v => v && setRelationshipLevels(v)} style={{ width: 140 }} addonAfter="hops" />
          </div>
        )}

        <Divider style={{ margin: '12px 0' }} />

        <Space>
          <Button type="primary" icon={<FilterOutlined />} onClick={applyFilter} disabled={checkedKeys.filter(k => !k.startsWith('schema:')).length === 0}>
            Apply Filter
          </Button>
          {activeFilter && (
            <Button icon={<ClearOutlined />} onClick={() => { clearFilter(); setFilterOpen(false); }}>
              Clear Filter
            </Button>
          )}
        </Space>

        {activeFilter && (
          <div style={{ marginTop: 12 }}>
            <Tag color="blue">
              Active: {activeFilter.tableIds.length} view(s) — {activeFilter.mode === 'connected_paths' ? 'Connected Paths' : `${activeFilter.levels} level(s)`}
            </Tag>
          </div>
        )}
      </Drawer>

      {/* ── ERD Canvas ── */}
      <div style={{ flex: 1, position: 'relative' }}>
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <Spin size="large" tip="Loading data model..." />
          </div>
        ) : filteredTables.length === 0 ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <Empty description={allTables.length === 0 ? 'No data model available. Run a catalog scan from the Data Explorer page first.' : 'No tables match your filter criteria.'} />
          </div>
        ) : (
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={handleNodesChange}
            onEdgesChange={onEdgesChange}
            nodeTypes={nodeTypes}
            fitView
            fitViewOptions={{ padding: 0.3 }}
            minZoom={0.05}
            maxZoom={2}
            defaultEdgeOptions={{ type: edgeType }}
            proOptions={{ hideAttribution: true }}
          >
            <Background color="#e8e8e8" gap={20} />
            <Controls />
            <MiniMap
              nodeColor={(node) => {
                if (node.type === 'schemaGroup') return 'rgba(22,119,255,0.08)';
                if (node.data?.highlighted) return '#1677ff';
                return TABLE_TYPE_COLORS[node.data?.tableType] || '#bbb';
              }}
              maskColor="rgba(0,0,0,0.1)"
              style={{ border: '1px solid var(--theme-border, #d9d9d9)' }}
            />
            {/* Chrome-like floating search — top right */}
            <Panel position="top-right">
              <div style={{
                display: 'flex',
                alignItems: 'center',
                border: '1px solid var(--theme-border, #d9d9d9)',
                borderRadius: 6,
                padding: '0 8px',
                background: 'var(--theme-cardBg, #fff)',
                height: 34,
                boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
              }}>
                <SearchOutlined style={{ color: '#999', marginRight: 6 }} />
                <input
                  type="text"
                  value={searchText}
                  onChange={e => setSearchText(e.target.value)}
                  onKeyDown={onSearchKeyDown}
                  placeholder="Find tables..."
                  style={{
                    border: 'none', outline: 'none', background: 'transparent',
                    width: 180, fontSize: 13, color: 'var(--theme-textPrimary, #333)',
                  }}
                />
                {searchText && (
                  <>
                    <span style={{ fontSize: 11, color: '#999', marginRight: 4, whiteSpace: 'nowrap' }}>
                      {searchMatches.length > 0 ? `${searchIdx + 1}/${searchMatches.length}` : '0/0'}
                    </span>
                    <Tooltip title="Previous (Shift+Enter)">
                      <Button type="text" size="small" icon={<UpOutlined style={{ fontSize: 10 }} />} onClick={goPrevMatch} disabled={searchMatches.length === 0} style={{ padding: '0 2px', minWidth: 20 }} />
                    </Tooltip>
                    <Tooltip title="Next (Enter)">
                      <Button type="text" size="small" icon={<DownOutlined style={{ fontSize: 10 }} />} onClick={goNextMatch} disabled={searchMatches.length === 0} style={{ padding: '0 2px', minWidth: 20 }} />
                    </Tooltip>
                    <Button type="text" size="small" icon={<CloseOutlined style={{ fontSize: 10 }} />} onClick={() => setSearchText('')} style={{ padding: '0 2px', minWidth: 20 }} />
                  </>
                )}
              </div>
            </Panel>
            <Panel position="bottom-left" style={{ fontSize: 11, color: '#999' }}>
              {visibleTables} of {allTables.length} tables · {visibleRelationships} of {allRelationships.length} relationships
            </Panel>
          </ReactFlow>
        )}
      </div>
    </div>
  );
}

function DataModel() {
  return (
    <ReactFlowProvider>
      <DataModelInner />
    </ReactFlowProvider>
  );
}

export default DataModel;